import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { EnvConfigurationService } from '../envconf/envconf.service';
import { ExportFileDto, ExportFilePage } from '../../models/export-file/export-file-dto';
import { Pagination } from '../../models/pagination';
import { PaginatedResponse, PaginatorFilters } from '@michelin/theme';
import { ApiService } from '../api/api.service';

@Injectable({ providedIn: 'root' })
export class ExportFileService {
  private endpoint = `${EnvConfigurationService.configuration.BACKEND_URL}${EnvConfigurationService.configuration.BACKEND_SUB_PATH}/export-files`;

  constructor(private readonly http: HttpClient, private readonly apiService: ApiService) {}

  getPaginatedResponse(pagination: Pagination<ExportFileDto>): PaginatedResponse<ExportFileDto> {
    return {
      count: pagination.totalElements,
      items: pagination.content
    };
  }

  search(automakerId: number, paginatorFilters?: PaginatorFilters): Observable<PaginatedResponse<ExportFileDto>> {
    return this.http
      .get<PaginatedResponse<ExportFileDto>>(
        `${this.endpoint}${this.apiService.getQueryParams(paginatorFilters) || '?'}&automakerId=${automakerId}`
      )
      .pipe(map((items: Pagination<ExportFileDto>) => this.getPaginatedResponse(items)));
  }

  list(automakerId: number, page: number = 0, size: number = 20): Observable<ExportFilePage> {
    const params: string[] = [];
    if (automakerId) {
      params.push(`automakerId=${automakerId}`);
    }
    params.push(`page=${page}`);
    params.push(`size=${size}`);
    const query = params.length ? `?${params.join('&')}` : '';
    return this.http.get<ExportFilePage>(`${this.endpoint}${query}`);
  }

  generate(automakerId: number): Observable<object> {
    return this.http.post<object>(`${this.endpoint}/generate/${automakerId}`, {});
  }

  download(id: number): Observable<{ blob: Blob; filename: string | null; response: HttpResponse<Blob> }> {
    return this.http.get(`${this.endpoint}/${id}/download`, { responseType: 'blob', observe: 'response' }).pipe(
      map((response: HttpResponse<Blob>) => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename: string | null = null;
        if (contentDisposition) {
          const match = /filename="([^"]+)"/i.exec(contentDisposition);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        return { blob: response.body!, filename, response };
      })
    );
  }
}
